import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Grid } from 'semantic-ui-react'
import { MaxValueLabel, MinValueLabel, SliderC, SliderWrapper } from '~/pages/Dairy/Survey/ShowSurvey/CreateEditQuestion/QuestionForms/Slider'
import { SliderContainer } from '../style'
import { KeyLabelType } from '~/types'
import { FormFields } from '~/components/FormFields'
import { formatNumberWithoutFractions } from '~/utils/helpers'
import TableRearing from './TableRearing'
import InfoPopup from '~/components/InfoPopup'

interface Props {
  calfRearingTypes: KeyLabelType[]
}

const CalfRearing: React.FC<Props> = (props) => {
  const { calfRearingTypes } = props
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()

  const watchWeaningWeeks = useWatch({ control, name: 'weaningWeeks' })
  const watchGendeRation = useWatch({ control, name: 'calvesSexRate' })

  return <Grid columns={4}>
    <Grid.Row>
      <Grid.Column>
        <SliderContainer>
          <Form.Field>
            <label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{t('Weaning age: {{rating}} Wochen', { rating: formatNumberWithoutFractions(watchWeaningWeeks) })}</div>
                <InfoPopup
                  content={t('If you temporarily drink whole milk and MAT combined, please select the product with the predominant proportion.')}
                  styleIcon={{ fontSize: '1.2em' }}
                  position={'top center'}
                />
              </div>
            </label>
          </Form.Field>
          <SliderWrapper>
            <MinValueLabel style={{ fontWeight: 'normal' }}>{t('min. Wo.')}</MinValueLabel>
            <SliderC
              step={1}
              rating={((watchWeaningWeeks - 4) / (11)) * 100}
              min={4}
              max={16}
              value={watchWeaningWeeks}
              onChange={(e) => setValue('weaningWeeks', e.target.value)}
            />
            <MaxValueLabel style={{ fontWeight: 'normal' }}>{t('max. Wo.')}</MaxValueLabel>
          </SliderWrapper>
        </SliderContainer>
      </Grid.Column>
      <Grid.Column width={10}>
        <TableRearing calfRearingTypes={calfRearingTypes} />
      </Grid.Column>
    </Grid.Row>
    <Grid.Row>
      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'maleCalfLiveWeight'}
          typeInput='controled'
          suffix=' kg'
          withoutAsterisk
          allowDecimals={true}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('Male Weight calf')}</label>
            <InfoPopup
              content={t('Weight at time of sale/transfer in fattening.')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </div>}
          noErrorMessage
          decimalScale={2}
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 40 || transformValue > 200) {
                return false
              }
              return true
            },
            required: true
          }}
          placeholder={t('Male Weight calf')}
        />
      </Grid.Column>

      <Grid.Column>
        <FormFields.Input
          control={control}
          typeInput='controled'
          withoutAsterisk
          allowDecimals={true}
          noErrorMessage
          suffix=' kg'
          decimalScale={2}
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 40 || transformValue > 200) {
                return false
              }
              return true
            },
            required: true
          }}
          name={'femaleCalfLiveWeight'}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('Female Weight calf')}</label>
            <InfoPopup
              content={t('Weight at the time of sale/transition into young cattle rearing.')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </div>}
          placeholder={t('Female Weight calf')}
        />
      </Grid.Column>
    </Grid.Row>

    <Grid.Row>
      <Grid.Column>
        <FormFields.Input
          control={control}
          name={'calvesStillbornAndDead'}
          withoutAsterisk
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 0 || transformValue > 40) {
                return false
              }
              return true
            },
            required: true
          }}
          noErrorMessage
          suffix=' %'
          allowDecimals
          typeInput={'controled'}
          decimalScale={2}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{t('Calves stillborn/died')}</label>
            <InfoPopup
              content={t('Source LKV; Stillbirths as in the LKV company comparison. Includes stillbirths + dies within 48 hours + dies from the 3rd day up \n\
              to maximum age female 6 weeks, male 30 days for Simmental and Gelbvieh, 21 days for Brown Swiss, 14 days for Holsteins.')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </div>}
          placeholder={t('Calves stillborn/died')}
        />
      </Grid.Column>
      <Grid.Column>
        <SliderContainer>
          <Form.Field label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{t('Gender ratio: {{rating}} %', { rating: formatNumberWithoutFractions(watchGendeRation) })}</div>
            <InfoPopup
              content={t('can be found in HIT or LKV')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </label>} />
          <SliderWrapper>
            <MinValueLabel style={{ fontWeight: 'normal' }}>{t('min. %')}</MinValueLabel>
            <SliderC
              step={1}
              rating={watchGendeRation}
              min={0}
              max={100}
              value={watchGendeRation}
              onChange={(e) => setValue('calvesSexRate', e.target.value)}
            />
            <MaxValueLabel style={{ fontWeight: 'normal' }}>{t('max. %')}</MaxValueLabel>
          </SliderWrapper>
        </SliderContainer>
      </Grid.Column>
    </Grid.Row>
  </Grid>
}
export default CalfRearing